import classNames from "classnames";
import countriesList from "i18n-iso-countries";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import {
  FC,
  memo,
  RefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ReactComponent as ArrowSVG } from "assets/images/common/arrow.svg";
import { ReactComponent as SearchSVG } from "assets/images/common/search.svg";

import useClickOutside from "hooks/useClickOutside";
import useDebounce from "hooks/useDebounce";

import TextInput, { TextInputPropsType } from "components/TextInput";
import { PhoneNumberCodeType } from "entities/common";

type PropsTypes = Omit<TextInputPropsType, "onChange" | "value"> & {
  onChange: (value: PhoneNumberCodeType) => void;
  value: Pick<PhoneNumberCodeType, "alpha3Code" | "callingCode">;
  /** When set, dropdown matches this element’s width and horizontal position (e.g. full `PhoneNumber` row). */
  alignRef?: RefObject<HTMLElement | null>;
};

type DropdownLayout = {
  target: HTMLElement;
  top: number;
  left: number;
  position: "absolute" | "fixed";
  width?: number;
};

const DROPDOWN_OFFSET_PX = 5;
/** Fallback until the portaled node is measured (matches `max-height` in phoneNumberCode.scss). */
const DROPDOWN_ESTIMATED_HEIGHT_PX = 210;

function shouldOpenDropdownUpward(spaceBelow: number, spaceAbove: number, dropdownHeight: number): boolean {
  if (dropdownHeight <= spaceBelow) return false;
  if (dropdownHeight <= spaceAbove) return true;
  return spaceAbove > spaceBelow;
}

function mergeDropdownLayout(prev: DropdownLayout | null, next: DropdownLayout): DropdownLayout {
  if (
    prev &&
    prev.target === next.target &&
    prev.top === next.top &&
    prev.left === next.left &&
    prev.position === next.position &&
    prev.width === next.width
  ) {
    return prev;
  }
  return next;
}

const PhoneNumberCode: FC<PropsTypes> = ({ onChange, value, className, alignRef, ...props }) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setOpen] = useState(false);
  const [dropdownLayout, setDropdownLayout] = useState<DropdownLayout | null>(null);
  const [search, setSearch] = useState("");
  const debouncedValue = useDebounce(search, 300);

  const countries = useMemo(() => getCountries(), []);

  const countryOptions = useMemo(
    () =>
      countries
        ?.map((countryCode) => {
          const callingCode = `+${getCountryCallingCode(countryCode)}`;
          const alpha3Code = getAlpha3Code(countryCode);

          return {
            alpha3Code,
            countryCode,
            callingCode,
            search: `${callingCode} ${alpha3Code}`,
          };
        })
        ?.filter((item) => !!item.alpha3Code)
        ?.sort((a, b) => a.alpha3Code.localeCompare(b.alpha3Code)),
    [countries],
  );

  const filteredOptions = useMemo(
    () =>
      countryOptions.filter((item) =>
        item.search?.toLowerCase()?.trim()?.includes(debouncedValue?.toLowerCase()?.trim()),
      ),
    [countryOptions, debouncedValue],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);

  const updateDropdownLayout = useCallback(() => {
    if (!anchorRef.current) return;
    const triggerRect = anchorRef.current.getBoundingClientRect();
    const alignEl = alignRef?.current ?? anchorRef.current;
    const alignRect = alignEl.getBoundingClientRect();
    const matchAlignWidth = !!alignRef;
    const dialog = anchorRef.current.closest("dialog");
    const offset = DROPDOWN_OFFSET_PX;
    const dropdownHeight = dropdownRef.current?.offsetHeight ?? DROPDOWN_ESTIMATED_HEIGHT_PX;
    const spaceBelow = window.innerHeight - triggerRect.bottom - offset;
    const spaceAbove = triggerRect.top - offset;
    const openUpward = shouldOpenDropdownUpward(spaceBelow, spaceAbove, dropdownHeight);

    if (dialog) {
      const dialogRect = dialog.getBoundingClientRect();
      const top = openUpward
        ? triggerRect.top - dialogRect.top - offset - dropdownHeight
        : triggerRect.bottom - dialogRect.top + offset;
      setDropdownLayout((prev) =>
        mergeDropdownLayout(prev, {
          target: dialog,
          top: Math.max(0, top),
          left: alignRect.left - dialogRect.left,
          position: "absolute",
          ...(matchAlignWidth ? { width: alignRect.width } : {}),
        }),
      );
    } else {
      const top = openUpward ? triggerRect.top - offset - dropdownHeight : triggerRect.bottom + offset;
      setDropdownLayout((prev) =>
        mergeDropdownLayout(prev, {
          target: document.body,
          top: Math.max(0, top),
          left: alignRect.left,
          position: "fixed",
          ...(matchAlignWidth ? { width: alignRect.width } : {}),
        }),
      );
    }
  }, [alignRef]);

  const handleToggle = () => setOpen((prevState) => !prevState);

  const handleSearch = (event: SyntheticEvent) => {
    setSearch((event.target as HTMLInputElement).value);
  };

  const handleSelect = (item: PhoneNumberCodeType) => () => {
    setSearch("");
    onChange(item);
    handleClose();
  };

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownLayout(null);
      return;
    }
    updateDropdownLayout();
    const rafId = window.requestAnimationFrame(() => updateDropdownLayout());
    return () => window.cancelAnimationFrame(rafId);
  }, [isOpen, updateDropdownLayout, filteredOptions.length, debouncedValue]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => updateDropdownLayout();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen, updateDropdownLayout]);

  useClickOutside(anchorRef, handleClose, dropdownRef);

  const dropdown =
    isOpen &&
    dropdownLayout &&
    createPortal(
      <div
        ref={dropdownRef}
        className={"phone-number__code__dropdown"}
        style={{
          top: dropdownLayout.top,
          left: dropdownLayout.left,
          position: dropdownLayout.position,
          ...(dropdownLayout.width != null
            ? { width: dropdownLayout.width, minWidth: dropdownLayout.width, maxWidth: dropdownLayout.width }
            : undefined),
          ...(dropdownLayout.position === "fixed" ? { zIndex: 100 } : undefined),
        }}
      >
        <TextInput
          value={search}
          variant="secondary"
          onChange={handleSearch}
          placeholder="Search"
          className={"phone-number__code__dropdown-input"}
          afterIcon={
            <span className="phone-number__code__dropdown-input-search">
              <SearchSVG />
            </span>
          }
        />

        <ul className={"phone-number__code__dropdown__list"}>
          {filteredOptions.map((item, index) => (
            <li
              onClick={handleSelect(item)}
              key={`${item.callingCode}-${index}`}
              className={classNames("phone-number__code__dropdown__list__item", {
                "phone-number__code__dropdown__list__item_selected": value.callingCode === item.callingCode,
              })}
            >
              <span className={`fi fi-${item.countryCode.toLowerCase()}`} />
              <span>{item.alpha3Code || item.countryCode}</span>
              <span>{item.callingCode}</span>
            </li>
          ))}
        </ul>
      </div>,
      dropdownLayout.target,
    );

  return (
    <div ref={anchorRef} className={classNames("phone-number__code", className)}>
      <div onClick={handleToggle}>
        <TextInput
          {...props}
          readOnly
          value={value.callingCode}
          label="Code"
          className={classNames("phone-number__code__input", {
            "phone-number__code__input_open": isOpen,
          })}
          afterIcon={
            <span
              className={classNames("phone-number__code__input__arrow", {
                "phone-number__code__input__arrow_open": isOpen,
              })}
            >
              <ArrowSVG />
            </span>
          }
        />
      </div>

      {dropdown}
    </div>
  );
};

function getAlpha3Code(alpha2: string): string {
  return countriesList.alpha2ToAlpha3(alpha2) || "";
}

export default memo(PhoneNumberCode);
